
import type { RelatedPrompts } from '@empathyco/playboard-core-domain'
import type { PropType, SetupContext } from 'vue'
import { computed, defineComponent, ref, watch } from 'vue'
import { useRelatedPrompts } from '../../composables'

import { RelatedPromptsTypeEnum } from '../../enums'
import { EpbButton } from '../shared/epb-button'
import { EpbSwitch } from '../shared/epb-switch'
import { EpbText } from '../shared/epb-text'
import RelatedPromptsPreviewItem from './related-prompts-preview-item.component.vue'

export default defineComponent({
  name: 'RelatedPromptsPill',
  components: { EpbButton, EpbSwitch, EpbText, RelatedPromptsPreviewItem },
  props: {
    item: {
      type: Object as PropType<RelatedPrompts>,
      required: true,
    },
    searchQuery: {
      type: String,
      required: true,
    },
    messages: {
      type: Object,
      required: true,
    },
    index: Number,
    selected: Boolean,
    disabled: Boolean,
  },
  emits: {
    setEnabled: (value: RelatedPrompts) => true,
    toggleExpanded: (item: RelatedPrompts, expanded: boolean) => true,
  },
  setup(props, ctx: SetupContext) {
    const baseClass = 'related-prompts-pill'
    const { emit } = ctx
    const { loadPreviewRelatedPromptsQueries } = useRelatedPrompts(ctx)

    const pillItem = ref(props.item)
    const renderedSuggestions = ref(new Set<number>())
    const expanded = ref(false)
    const hasBeenAnimated = ref(false)
    const relatedPromptsPreviews = ref([])

    const pillClasses = computed(() => [
      baseClass,
      { [`${baseClass}--animation`]: !hasBeenAnimated.value },
      { [`${baseClass}--disabled`]: props.disabled },
      { [`${baseClass}--selected`]: props.selected },
      { [`${baseClass}--expanded`]: expanded.value },
    ])

    const textAnimationStyle = computed(() => `--n: ${pillItem.value.suggestionText.length}`)

    const baseTextClasses = computed(() => [
      `${baseClass}__text`,
      {
        [`${baseClass}__text--animation`]:
          !hasBeenAnimated.value && !renderedSuggestions.value.has(props.index),
      },
    ])

    const headerTextClasses = computed(() => [
      ...baseTextClasses.value,
      `${baseClass}__text--header`,
    ])

    const setEnabled = (value: boolean): void => {
      pillItem.value.active = value
      emit('setEnabled', pillItem.value)
    }

    const markAsRendered = (index: number): void => {
      renderedSuggestions.value.add(index)
    }

    const toggleExpanded = async (): Promise<void> => {
      expanded.value = !expanded.value
      emit('toggleExpanded', pillItem.value, expanded.value)

      if (expanded.value) {
        await loadPreviewData()
      }
    }

    const loadPreviewData = async (): Promise<void> => {
      try {
        const previewPromises = props.item.nextQueries.map(query =>
          loadPreviewRelatedPromptsQueries(query),
        )
        relatedPromptsPreviews.value = await Promise.all(previewPromises)
      } catch (error) {
        relatedPromptsPreviews.value = []
      }
    }

    watch(
      () => props.item,
      newItem => {
        pillItem.value = newItem
      },
      { immediate: true },
    )

    return {
      baseClass,
      RelatedPromptsTypeEnum,
      pillItem,
      renderedSuggestions,
      expanded,
      hasBeenAnimated,
      relatedPromptsPreviews,
      pillClasses,
      textAnimationStyle,
      headerTextClasses,
      baseTextClasses,
      setEnabled,
      markAsRendered,
      toggleExpanded,
    }
  },
})
